import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { PaymentStatus } from "@/types/form-types"
import { format } from "date-fns"
import { CheckCircle2Icon, Info } from "lucide-react"

type Props = {
	payment_type: "self" | "loan" | "offline"
	status: PaymentStatus
	icon: React.ReactNode
	iconBg: string
	heading: string
	message: string
	mapEntries: Record<string, React.ReactNode>
	hasDownloads: boolean
	DownloadComponent?: React.FC
	updated_at?: string
	remarks?: string
}

const SidePerforatedSeparator = () => (
	<div className="relative my-6 flex items-center justify-between">
		{/* Left Circle */}
		<div className="absolute left-0 flex h-10 w-5 items-center justify-center">
			<div className="h-full w-full rounded-r-full bg-card"></div>
		</div>

		{/* Dotted Line */}
		<div
			className="mx-6 h-[1px] w-[calc(100%-4rem)] sm:mx-8"
			style={{
				backgroundImage:
					"repeating-linear-gradient(to right, #d1d5db 0, #d1d5db 8px, transparent 8px, transparent 16px)",
				backgroundSize: "100% 1px",
				backgroundRepeat: "no-repeat",
				backgroundPosition: "center",
			}}
		></div>

		{/* Right Circle */}
		<div className="absolute right-0 flex h-10 w-5 items-center justify-center">
			<div className="h-full w-full rounded-l-full bg-card"></div>
		</div>
	</div>
)

const BottomPerforations = () => {
	const PERFORATION_COUNT = 15

	return (
		<div
			className="absolute bottom-0 left-0 right-0 h-8"
			style={{ transform: "translateY(50%)" }}
		>
			<div className="flex h-full w-full justify-between px-1">
				{Array.from({ length: PERFORATION_COUNT }).map((_, index) => (
					<div
						key={index}
						className="h-8 w-8 flex-shrink-0 rounded-full bg-card"
					/>
				))}
			</div>
		</div>
	)
}

export const StatusAlert = ({
	updated_at,
	remarks,
	status,
}: {
	updated_at?: string
	remarks: string
	status: PaymentStatus
}) => {
	const getParams = () => {
		switch (status) {
			case PaymentStatus.OFFLINE_PAYMENT_PENDING_VERIFICATION:
				return {
					icon: <Info className={`text-yellow-600`} />,
					theme: {
						text: "text-yellow-800",
						bg: "bg-yellow-50",
					},
					title: "Your payment is currently under review.",
					subtitle:
						"Our team will verify your Application proof within 24-48 hours. You'll receive a confirmation email once verified.",
				}
			case PaymentStatus.OFFLINE_PAYMENT_APPROVED:
				return {
					icon: <CheckCircle2Icon className={`text-green-600`} />,
					theme: {
						text: "text-green-800",
						bg: "bg-green-50",
					},
					title: "Your offline payment has been successfully verified.",
					subtitle: `Verified by Finance Team on ${format(
						updated_at || "",
						"dd MMM, yyyy 'at' h:mm a",
					)}. You can now proceed to the next step.`,
				}
			case PaymentStatus.OFFLINE_PAYMENT_REJECTED:
				return {
					icon: <Info className={`text-red-600`} />,
					theme: {
						text: "text-red-800",
						bg: "bg-red-50",
					},
					title: "Your payment could not be verified.",
					subtitle: (
						<>
							<span className="font-bold">Reason</span> :{" "}
							{remarks}. <br />
						</>
					),
				}
			case PaymentStatus.OFFLINE_LOAN_PENDING_VERIFICATION:
				return {
					icon: <Info className={`text-yellow-600`} />,
					theme: {
						text: "text-yellow-800",
						bg: "bg-yellow-50",
					},
					title: "Your Offline Loan Application is currently under review.",
					subtitle:
						"Our team will verify your Loan Application proof within 24-48 hours. You'll receive a confirmation email once verified.",
				}
			case PaymentStatus.OFFLINE_LOAN_APPROVED:
				return {
					icon: <CheckCircle2Icon className={`text-green-600`} />,
					theme: {
						text: "text-green-800",
						bg: "bg-green-50",
					},
					title: "Your offline Loan Application has been successfully verified.",
					subtitle: `Verified by Finance Team on ${format(
						updated_at || "",
						"dd MMM, yyyy 'at' h:mm a",
					)}. You can now proceed to the next step.`,
				}
			case PaymentStatus.OFFLINE_LOAN_REJECTED:
				return {
					icon: <Info className={`text-red-600`} />,
					theme: {
						text: "text-red-800",
						bg: "bg-red-50",
					},
					title: "Your offline Loan Details could not be verified.",
					subtitle: (
						<>
							<span className="font-bold">Reason</span> :{" "}
							{remarks}. <br />
						</>
					),
				}
			default:
				return {
					icon: <Info className={`text-yellow-600`} />,
					theme: {
						text: "text-yellow-800",
						bg: "bg-yellow-50",
					},
					title: "Your payment is currently under review.",
					subtitle:
						"Our team will verify your Application proof within 24-48 hours. You'll receive a confirmation email once verified.",
				}
		}
	}
	return (
		<div className={cn("flex items-center gap-2 px-8 pb-0 sm:p-10")}>
			<Alert
				className={cn(
					"flex h-full w-full gap-2 border-none",
					getParams().theme.bg,
				)}
			>
				<div>{getParams().icon}</div>
				<div className="flex flex-col space-y-3">
					<AlertTitle
						className={cn("text-left", getParams().theme.text)}
					>
						{getParams().title}
					</AlertTitle>
					<AlertDescription
						className={cn("text-left", getParams().theme.text)}
					>
						{getParams().subtitle}
					</AlertDescription>
				</div>
			</Alert>
		</div>
	)
}

const StatusDownload = ({
	children,
	onClick,
}: {
	children: React.ReactNode
	onClick: () => void
}) => {
	return (
		<Button
			type="button"
			variant="outline"
			className="gap-2"
			onClick={onClick}
		>
			{children}
		</Button>
	)
}

const Status = ({
	heading,
	message,
	status,
	mapEntries,
	hasDownloads,
	DownloadComponent,
	remarks,
	icon,
	payment_type,
	iconBg,
	updated_at,
}: Props) => {
	const mapObject = Object.entries(mapEntries)
	return (
		<div className="relative w-full overflow-hidden rounded-tl-xl rounded-tr-xl bg-white text-center">
			{/* Header Content */}
			<div className="p-5 pb-0 sm:p-10">
				<div className="mb-6">
					<div
						className={cn(
							"mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-green-100",
							iconBg,
						)}
					>
						<div
							className={cn(
								"flex h-14 w-14 items-center justify-center rounded-full shadow-lg",
							)}
						>
							{icon}
						</div>
					</div>
					<h1 className="mb-2 text-3xl font-extrabold text-gray-800">
						{heading}
					</h1>
					<p className="text-base text-gray-600">{message}</p>
				</div>
			</div>
			{payment_type === "offline" && (
				<StatusAlert
					updated_at={updated_at}
					remarks={remarks || ""}
					status={status}
				/>
			)}

			<SidePerforatedSeparator />
			{/* Details Content */}
			<div className="p-8 pb-12 pt-0 sm:p-10">
				<div className="mb-6 text-left">
					<div className="grid grid-cols-2 gap-4">
						{mapObject?.map(([key, value], index) => (
							<div
								className={cn(
									"flex flex-col space-y-1",
									index % 2 === 0
										? "text-left"
										: "text-right",
								)}
								key={key}
							>
								<div className="text-xs uppercase tracking-wider text-gray-400">
									{key}
								</div>
								<div className="text-sm font-medium text-gray-700">
									{value}
								</div>
							</div>
						))}
					</div>
				</div>
				<div
					className="relative mx-[-2rem] mt-8 px-8 pt-6 sm:mx-[-2.5rem] sm:px-10"
					style={{
						backgroundImage:
							"repeating-linear-gradient(to right, #d1d5db 0, #d1d5db 8px, transparent 8px, transparent 16px)",
						backgroundSize: "100% 1px",
						backgroundRepeat: "no-repeat",
						backgroundPosition: "left top",
					}}
				>
					{hasDownloads && DownloadComponent && <DownloadComponent />}
				</div>
			</div>
			<BottomPerforations />
		</div>
	)
}

Status.Download = StatusDownload

export default Status
